/**
 * Button - Componente reutilizable estilo PlanetMoji
 * Gradiente en ángulo (135deg), buen contraste, DRY
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn'

export interface ButtonProps {
  variant?: 'primary' | 'primarySolid' | 'secondary' | 'outline' | 'ghost' | 'success'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  as?: 'button' | 'link'
  to?: string
  children: React.ReactNode
  className?: string
  disabled?: boolean
  onClick?: () => void
}

const variantStyles = {
  primary: 'bg-white/30 backdrop-blur-xl border border-white/50 text-white font-bold shadow-xl shadow-cyan-900/30 hover:bg-white/40 hover:border-white/70 hover:shadow-2xl active:scale-[0.98] bg-[linear-gradient(135deg,rgba(6,182,212,0.7)_0%,rgba(14,165,233,0.8)_50%,rgba(2,132,199,0.7)_100%)]',
  primarySolid: 'bg-[linear-gradient(135deg,#06b6d4_0%,#0891b2_50%,#0e7490_100%)] text-white font-bold shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:brightness-110 active:scale-[0.98] border border-cyan-400/50 backdrop-blur-sm',
  secondary: 'bg-white/25 backdrop-blur-xl border border-white/40 text-white font-bold shadow-lg hover:bg-white/35 bg-[linear-gradient(135deg,rgba(124,58,237,0.6)_0%,rgba(109,40,217,0.7)_100%)] active:scale-[0.98]',
  success: 'bg-white/30 backdrop-blur-xl border border-emerald-400/60 text-white font-bold shadow-lg bg-[linear-gradient(135deg,rgba(16,185,129,0.7)_0%,rgba(5,150,105,0.8)_100%)] hover:brightness-110 active:scale-[0.98]',
  outline: 'bg-white/10 backdrop-blur-md border-2 border-white/50 text-white font-bold hover:bg-white/20 active:scale-[0.98]',
  ghost: 'bg-transparent text-gray-700 font-bold hover:bg-gray-100 active:scale-[0.98]',
}

const sizeStyles = {
  sm: 'py-2 px-4 text-sm rounded-xl',
  md: 'py-2.5 px-5 text-base rounded-xl',
  lg: 'py-3 px-6 text-lg rounded-2xl',
}

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', fullWidth, as = 'button', to, children, className, disabled, onClick, ...props }, ref) => {
    const baseClasses = cn(
      'inline-flex items-center justify-center gap-2 transition-all duration-200',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      variantStyles[variant],
      sizeStyles[size],
      fullWidth && 'w-full',
      className
    )

    if (as === 'link' && to) {
      return (
        <Link to={to} className={baseClasses}>
          {children}
        </Link>
      )
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={baseClasses}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
